import { useParams, useNavigate } from 'react-router-dom'
import { useAuthStore } from '../../stores/authStore'
import { useEffect, useState } from 'react'

interface Event {
  id: string
  title: string
  description: string
  options: Array<{
    id: number
    text: string
    impact_description?: string
  }>
  event_type: string
  priority: string
}

interface DiplomaticEvent {
  id: string
  title: string
  description: string
  country_involved: string
  event_type: string
  options: Array<{
    id: number
    text: string
    impact_description?: string
  }>
}

function EventsPage() {
  const { gameId } = useParams<{ gameId: string }>()
  const navigate = useNavigate()
  const { token } = useAuthStore()
  const [events, setEvents] = useState<Event[]>([])
  const [diplomaticEvents, setDiplomaticEvents] = useState<DiplomaticEvent[]>([])
  const [loading, setLoading] = useState(true)
  const [processingEvent, setProcessingEvent] = useState<string | null>(null)
  const [message, setMessage] = useState<string | null>(null)

  useEffect(() => {
    fetchEvents()
  }, [gameId])

  const fetchEvents = async () => {
    try {
      setLoading(true)
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/events/game/${gameId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      if (response.ok) {
        const data = await response.json()
        setEvents(data.events || [])
        setDiplomaticEvents(data.diplomaticEvents || [])
      } else {
        setMessage('Failed to load events')
      }
    } catch (error) {
      setMessage('Error loading events')
    } finally {
      setLoading(false)
    }
  }

  const handleEventResponse = async (eventId: string, selectedOption: number, isDiplomatic: boolean = false) => {
    try {
      setProcessingEvent(eventId)
      const endpoint = isDiplomatic ? 'diplomatic-events' : 'events'
      
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/${endpoint}/${eventId}/respond`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ selectedOption })
      })

      const data = await response.json()
      if (data.success) {
        setMessage('Event resolved successfully!')
        // Refresh events after successful response
        await fetchEvents()
      } else {
        setMessage(data.error || 'Failed to process event response')
      }
    } catch (error) {
      setMessage('Error processing event response')
    } finally {
      setProcessingEvent(null)
    }
  }

  const getPriorityColor = (priority: string) => {
    switch (priority?.toLowerCase()) {
      case 'high': return 'border-red-300 bg-red-50'
      case 'medium': return 'border-yellow-300 bg-yellow-50'
      case 'low': return 'border-green-300 bg-green-50'
      default: return 'border-gray-300 bg-gray-50'
    }
  }

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="flex items-center justify-center h-64">
          <div className="text-lg text-gray-600">Loading events...</div>
        </div>
      </div>
    )
  }

  const totalEvents = events.length + diplomaticEvents.length

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Current Events</h1>
        <button 
          onClick={() => navigate(`/game/${gameId}`)}
          className="btn btn-secondary"
        >
          Back to Game
        </button>
      </div>

      {message && (
        <div className={`mb-6 p-4 rounded-lg ${message.includes('success') ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
          {message}
        </div>
      )}

      {totalEvents === 0 ? (
        <div className="card text-center py-12">
          <h2 className="text-xl font-semibold text-gray-900 mb-2">No Current Events</h2>
          <p className="text-gray-600">All events have been resolved. You can advance to the next turn.</p>
        </div>
      ) : (
        <div className="space-y-6">
          {/* Domestic Events */}
          {events.length > 0 && (
            <div>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">Domestic Events</h2>
              <div className="space-y-4">
                {events.map(event => (
                  <div key={event.id} className={`card border-2 ${getPriorityColor(event.priority)}`}>
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">{event.title}</h3>
                        <span className={`inline-block px-2 py-1 rounded text-xs font-medium ${
                          event.priority === 'high' ? 'bg-red-100 text-red-800' :
                          event.priority === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-green-100 text-green-800'
                        }`}>
                          {event.priority} Priority
                        </span>
                      </div>
                    </div>
                    
                    <p className="text-gray-700 mb-6">{event.description}</p>
                    
                    <div className="space-y-3">
                      <h4 className="font-medium text-gray-900">Choose your response:</h4>
                      {event.options?.map(option => (
                        <button
                          key={option.id}
                          onClick={() => handleEventResponse(event.id, option.id)}
                          disabled={processingEvent === event.id}
                          className="w-full text-left p-4 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <div className="font-medium text-gray-900">{option.text}</div>
                          {option.impact_description && (
                            <div className="text-sm text-gray-600 mt-1">{option.impact_description}</div>
                          )}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Diplomatic Events */}
          {diplomaticEvents.length > 0 && (
            <div>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">International Affairs</h2>
              <div className="space-y-4">
                {diplomaticEvents.map(event => (
                  <div key={event.id} className="card border-2 border-blue-300 bg-blue-50">
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">{event.title}</h3>
                        <span className="inline-block px-2 py-1 rounded text-xs font-medium bg-blue-100 text-blue-800">
                          {event.country_involved} • Diplomatic
                        </span>
                      </div>
                    </div>
                    
                    <p className="text-gray-700 mb-6">{event.description}</p>
                    
                    <div className="space-y-3">
                      <h4 className="font-medium text-gray-900">Choose your response:</h4>
                      {event.options?.map(option => (
                        <button
                          key={option.id}
                          onClick={() => handleEventResponse(event.id, option.id, true)}
                          disabled={processingEvent === event.id}
                          className="w-full text-left p-4 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <div className="font-medium text-gray-900">{option.text}</div>
                          {option.impact_description && (
                            <div className="text-sm text-gray-600 mt-1">{option.impact_description}</div>
                          )}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default EventsPage